import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

function parseUtcDate(value) {
  if (!value) return null;
  const raw = String(value);
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    const year = Number(dateOnly[1]);
    const month = Number(dateOnly[2]);
    const day = Number(dateOnly[3]);
    return new Date(Date.UTC(year, month - 1, day));
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseEventDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtMeetingDay(iso) {
  const d = parseEventDate(iso);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function fmtMeetingTime(iso) {
  const d = parseEventDate(iso);
  if (!d) return "—";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function shortMeetingLabel(title) {
  const parts = String(title || "").split("—");
  return (parts[1] || parts[0] || "Meeting").trim();
}

function timelineWindow(client, activeCycle) {
  if (client?.engagement_type === "monthly" && activeCycle) {
    const start = parseUtcDate(activeCycle.start_date || activeCycle.range_start);
    const end = parseUtcDate(activeCycle.end_date || activeCycle.range_end);
    if (start && end) return { start, end };
  }
  const start = parseUtcDate(client?.timeline_start);
  const end = parseUtcDate(client?.timeline_end);
  if (start && end) return { start, end };
  return null;
}

function inTimelineWindow(meeting, window) {
  if (!window) return true;
  const start = parseEventDate(meeting.start);
  if (!start) return false;
  const t = start.getTime();
  const from = new Date(window.start);
  from.setHours(0, 0, 0, 0);
  const to = new Date(window.end);
  to.setHours(23, 59, 59, 999);
  return t >= from.getTime() && t <= to.getTime();
}

/** Admin Meet — timeline meetings list + notes panel that fills mobile height. */
export default function MeetPage() {
  const { client, user } = useAuth();
  const clientId = client?.id;
  const [meetings, setMeetings] = useState([]);
  const [activeCycle, setActiveCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [meetingNotesDraft, setMeetingNotesDraft] = useState("");
  const [notesSaveState, setNotesSaveState] = useState("idle"); // idle | saving | saved | error
  const draftRef = useRef("");
  const selectedIdRef = useRef(null);
  const saveTimerRef = useRef(null);

  const load = useCallback(async () => {
    if (!clientId || user?.role !== "admin") return;
    setLoading(true);
    try {
      try {
        await api.post(`/admin/google-calendar/sync?client_id=${clientId}`);
      } catch {
        // Best-effort; list still loads from last sync.
      }
      const [calRes, dashRes] = await Promise.all([
        api.get("/portal/calendar", { params: { client_id: clientId } }),
        api.get("/portal/dashboard", { params: { client_id: clientId } }).catch(() => ({ data: null })),
      ]);
      const events = (Array.isArray(calRes.data) ? calRes.data : []).filter(
        (e) => e.type !== "milestone" && !String(e.id || "").startsWith("milestone-"),
      );
      setMeetings(events);
      setActiveCycle(dashRes.data?.active_cycle || null);
    } catch {
      toast.error("Could not load meetings");
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, user?.role]);

  useEffect(() => {
    load();
  }, [load]);

  const activeWindow = useMemo(() => timelineWindow(client, activeCycle), [client, activeCycle]);

  const timelineMeetings = useMemo(() => {
    const now = Date.now();
    return [...meetings]
      .filter((m) => inTimelineWindow(m, activeWindow))
      .sort((a, b) => {
        const aStart = parseEventDate(a.start)?.getTime() ?? 0;
        const bStart = parseEventDate(b.start)?.getTime() ?? 0;
        const aUpcoming = aStart >= now;
        const bUpcoming = bStart >= now;
        if (aUpcoming !== bUpcoming) return aUpcoming ? -1 : 1;
        return aUpcoming ? aStart - bStart : bStart - aStart;
      });
  }, [meetings, activeWindow]);

  const selectedMeeting = useMemo(
    () => timelineMeetings.find((m) => m.id === selectedMeetingId) || null,
    [timelineMeetings, selectedMeetingId],
  );

  useEffect(() => {
    draftRef.current = meetingNotesDraft;
  }, [meetingNotesDraft]);

  useEffect(() => {
    selectedIdRef.current = selectedMeetingId;
  }, [selectedMeetingId]);

  const persistMeetingNotes = useCallback(
    async (meetingId, text) => {
      if (!clientId || !meetingId) return false;
      setNotesSaveState("saving");
      try {
        await api.patch(
          `/portal/calendar/${meetingId}`,
          { meeting_notes: text || "" },
          { params: { client_id: clientId } },
        );
        setMeetings((prev) =>
          prev.map((m) => (m.id === meetingId ? { ...m, meeting_notes: text || "" } : m)),
        );
        setNotesSaveState("saved");
        return true;
      } catch (err) {
        setNotesSaveState("error");
        toast.error(err.response?.data?.detail || "Could not save notes");
        return false;
      }
    },
    [clientId],
  );

  const flushMeetingNotes = useCallback(async () => {
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const meetingId = selectedIdRef.current;
    if (!clientId || !meetingId) return;
    const text = draftRef.current || "";
    const current = meetings.find((m) => m.id === meetingId);
    if (!current || (current.meeting_notes || "") === text) return;
    await persistMeetingNotes(meetingId, text);
  }, [clientId, meetings, persistMeetingNotes]);

  useEffect(() => {
    if (!timelineMeetings.length) {
      setSelectedMeetingId(null);
      setMeetingNotesDraft("");
      return;
    }
    if (!selectedMeetingId || !timelineMeetings.some((m) => m.id === selectedMeetingId)) {
      const now = Date.now();
      const nextUp =
        timelineMeetings.find((m) => (parseEventDate(m.start)?.getTime() ?? 0) >= now) ||
        timelineMeetings[0];
      setSelectedMeetingId(nextUp.id);
      setMeetingNotesDraft(nextUp.meeting_notes || "");
      setNotesSaveState("idle");
    }
  }, [timelineMeetings, selectedMeetingId]);

  useEffect(() => {
    if (selectedMeeting) {
      setMeetingNotesDraft(selectedMeeting.meeting_notes || "");
      setNotesSaveState("idle");
    }
  }, [selectedMeeting?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosave meeting notes after a short pause.
  useEffect(() => {
    if (!clientId || !selectedMeetingId || !selectedMeeting) return undefined;
    if ((meetingNotesDraft || "") === (selectedMeeting.meeting_notes || "")) return undefined;
    setNotesSaveState((prev) => (prev === "saving" ? prev : "idle"));
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null;
      persistMeetingNotes(selectedMeetingId, meetingNotesDraft || "");
    }, 650);
    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [
    clientId,
    selectedMeetingId,
    meetingNotesDraft,
    selectedMeeting?.meeting_notes,
    persistMeetingNotes,
  ]);

  useEffect(() => {
    return () => {
      // Best-effort flush on leave (fire-and-forget).
      const meetingId = selectedIdRef.current;
      const text = draftRef.current || "";
      if (!clientId || !meetingId) return;
      api
        .patch(
          `/portal/calendar/${meetingId}`,
          { meeting_notes: text },
          { params: { client_id: clientId } },
        )
        .catch(() => {});
    };
  }, [clientId]);

  const selectMeeting = async (meeting) => {
    if (meeting.id === selectedMeetingId) return;
    await flushMeetingNotes();
    setSelectedMeetingId(meeting.id);
    setMeetingNotesDraft(meeting.meeting_notes || "");
    setNotesSaveState("idle");
  };

  if (!client) {
    return <div className="p-6 sm:p-12 t-meta">No client context.</div>;
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-5 md:p-7" data-testid="meet-page">
        <p className="t-meta">Loading meetings…</p>
      </div>
    );
  }

  const saveLabel =
    notesSaveState === "saving"
      ? "Saving…"
      : notesSaveState === "saved"
        ? "Saved"
        : notesSaveState === "error"
          ? "Couldn’t save"
          : null;

  return (
    <div
      className="flex min-h-0 max-w-full flex-1 flex-col gap-3 overflow-x-clip p-4 t-fade-up sm:gap-4 sm:p-5 md:p-7 max-lg:h-[calc(100dvh-var(--portal-mobile-header-h,68px)-1.5rem)] max-lg:max-h-[calc(100dvh-var(--portal-mobile-header-h,68px)-1.5rem)] max-lg:overflow-hidden"
      data-testid="meet-page"
    >
      <section className="neo-panel shrink-0 p-5 md:p-6" data-testid="meet-list-panel">
        <div className="label-eyebrow mb-4">Meetings</div>

        {timelineMeetings.length === 0 ? (
          <div className="t-meta">No meetings in this timeline.</div>
        ) : (
          <div data-testid="meet-list">
            <ul className="divide-y divide-black/5">
              {timelineMeetings.map((m) => {
                const active = m.id === selectedMeetingId;
                const upcoming = (parseEventDate(m.start)?.getTime() ?? 0) >= Date.now();
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => selectMeeting(m)}
                      className={`flex w-full items-baseline justify-between gap-3 py-2.5 text-left first:pt-0 last:pb-0 ${
                        active
                          ? "text-[var(--t-text)]"
                          : upcoming
                            ? "text-[var(--t-text)]/80"
                            : "text-[var(--t-meta)]"
                      }`}
                      data-testid={`meet-item-${m.id}`}
                    >
                      <span className="font-heading min-w-0 truncate text-sm !font-light">
                        {shortMeetingLabel(m.title)}
                      </span>
                      <span className="shrink-0 text-[11px] tabular-nums">
                        {fmtMeetingDay(m.start)} · {fmtMeetingTime(m.start)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      <section
        className="neo-panel flex min-h-0 flex-1 flex-col p-5 md:p-6 max-lg:min-h-0"
        data-testid="meet-notes"
      >
        <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
          <div className="label-eyebrow mb-0">Notes</div>
          {selectedMeeting && saveLabel ? (
            <span
              className={`text-[11px] tracking-[0.04em] ${
                notesSaveState === "error" ? "text-[var(--t-error)]" : "text-[var(--t-meta)]"
              }`}
              data-testid="meet-notes-save-status"
            >
              {saveLabel}
            </span>
          ) : null}
        </div>
        {selectedMeeting ? (
          <textarea
            className="t-scroll-soft min-h-[140px] w-full flex-1 resize-none border-0 bg-transparent p-0 text-sm leading-relaxed outline-none placeholder:text-[var(--t-meta)]/55 max-lg:min-h-0"
            value={meetingNotesDraft}
            onChange={(e) => setMeetingNotesDraft(e.target.value)}
            placeholder="Soft space for what mattered on this call…"
            data-testid="meet-notes-input"
          />
        ) : (
          <div className="t-meta flex-1">Select a meeting to take notes.</div>
        )}
      </section>
    </div>
  );
}
